import { useEffect, useRef, useState } from 'react';

import { OtpInputWrapper } from './Otp.styles';

const INITIAL_TIME = 60;

function OtpInput({
  numInputs = 5,
  inputWidth,
  width,
  inputBg,
  handleResentOtp,
  OTPError,
  setOTPError,
  setOtpValue = () => {},
}) {
  const [timeLeft, setTimeLeft] = useState(INITIAL_TIME);
  const [otp, setOtp] = useState(() => Array(numInputs).fill(''));
  const [runAgain, setRunAgain] = useState(false);
  const refs = useRef([]);

  const updateOtp = next => {
    setOtp(next);
    setOtpValue(next);
  };

  const restartTimer = async () => {
    await handleResentOtp();
    setRunAgain(prev => !prev);
    setTimeLeft(INITIAL_TIME);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        if (prevTime <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [runAgain]);

  const formatTime = seconds => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  const handleChange = (index, rawValue) => {
    setOTPError(false);
    const value = rawValue.replace(/\D/g, '');

    if (otp[index] !== '' && index + 1 === numInputs) {
      return;
    }

    if (otp[index] !== '' && value.length > 1) {
      const next = [...otp];
      next[index + 1] = value.slice(-1);
      updateOtp(next);
      refs.current[index + 1]?.focus();
      return;
    }

    if (/^\d?$/.test(value)) {
      const next = [...otp];
      next[index] = value;
      updateOtp(next);

      if (value) {
        refs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === 'Backspace') {
      event.preventDefault();
      const next = [...otp];
      next[index] = '';
      updateOtp(next);
      refs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event, index) => {
    event.preventDefault();
    setOTPError(false);

    const pasteData = event.clipboardData.getData('text').replace(/\D/g, '');
    if (!pasteData) return;

    const next = [...otp];
    pasteData
      .slice(0, numInputs - index)
      .split('')
      .forEach((digit, offset) => {
        next[index + offset] = digit;
      });

    updateOtp(next);

    const lastIndex = Math.min(index + pasteData.length - 1, numInputs - 1);
    refs.current[lastIndex]?.focus();
  };

  return (
    <OtpInputWrapper $width={width} $inputWidth={inputWidth} $inputBg={inputBg}>
      <div className="otp-fields">
        {otp.map((value, index) => (
          <input
            key={index}
            ref={element => {
              refs.current[index] = element;
            }}
            className={otp[index] === '' && OTPError ? 'error' : ''}
            placeholder="-"
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            pattern="[0-9]*"
            maxLength={1}
            value={value}
            onChange={event => handleChange(index, event.target.value)}
            onKeyDown={event => handleKeyDown(index, event)}
            onPaste={event => handlePaste(event, index)}
          />
        ))}
      </div>

      <div className="timer">
        <button
          type="button"
          disabled={timeLeft !== 0}
          className={timeLeft !== 0 ? 'resend-code disabled' : 'resend-code'}
          onClick={restartTimer}>
          Resend code
        </button>
        <span className="time-left">{formatTime(timeLeft)}</span>
      </div>
    </OtpInputWrapper>
  );
}

export default OtpInput;
